import { Box, ButtonBase } from "@mui/material";
import InfoIcon from "@mui/icons-material/Info";
import CheckCircleIcon from "@mui/icons-material/CheckCircle";
import ErrorIcon from "@mui/icons-material/Error";
import StarsIcon from "@mui/icons-material/Stars";
import CloseIcon from "@mui/icons-material/Close";
import { colorPalette } from "../../config/colorPalette";

// bg / border: banner surface. solid: icon and link. content: title and body text.
const TONES = {
  info: {
    bg: colorPalette.cerulean[100],
    border: colorPalette.cerulean[300],
    solid: colorPalette.cerulean[700],
    content: colorPalette.cerulean[1000],
    Icon: InfoIcon,
    role: "status",
  },
  success: {
    bg: colorPalette.salem[100],
    border: colorPalette.salem[300],
    solid: colorPalette.salem[700],
    content: colorPalette.salem[1000],
    Icon: CheckCircleIcon,
    role: "status",
  },
  warning: {
    bg: colorPalette.yellow[100],
    border: colorPalette.yellow[300],
    solid: colorPalette.yellow[700],
    content: colorPalette.yellow[1000],
    Icon: ErrorIcon,
    role: "alert",
  },
  error: {
    bg: colorPalette.red[100],
    border: colorPalette.red[300],
    solid: colorPalette.red[700],
    content: colorPalette.red[1000],
    Icon: ErrorIcon,
    role: "alert",
  },
  promo: {
    bg: colorPalette.purple[100],
    border: colorPalette.purple[300],
    solid: colorPalette.purple[700],
    content: colorPalette.purple[1000],
    Icon: StarsIcon,
    role: "status",
  },
};

const linkSx = (color) => ({
  alignSelf: "flex-start",
  fontFamily: '"Lexend", sans-serif',
  fontSize: 12,
  fontWeight: 400,
  lineHeight: 1.35,
  letterSpacing: "0.15px",
  textDecoration: "underline",
  color,
});

/**
 * Banner
 *
 * Props:
 * - tone: info | success | warning | error | promo
 * - title: bold first line
 * - children: body text
 * - action: { label, onClick?, href? } underlined link under the body
 * - onDismiss: shows the close button when provided
 * - icon: replaces the default tone icon
 * - sx: extra styles, merged last
 */
const Banner = ({
  tone = "info",
  title,
  children,
  action,
  onDismiss,
  icon,
  sx: sxOverride = {},
  ...rest
}) => {
  const style = TONES[tone] || TONES.info;
  const { Icon } = style;

  return (
    <Box
      role={style.role}
      {...rest}
      sx={{
        display: "flex",
        alignItems: "flex-start",
        gap: "12px",
        padding: "11px 15px", // Figma's 12px / 16px, less the 1px border
        borderRadius: "12px",
        boxSizing: "border-box",
        backgroundColor: style.bg,
        border: `1px solid ${style.border}`,
        color: style.content,
        ...sxOverride,
      }}
    >
      <Box
        component="span"
        aria-hidden="true"
        sx={{
          display: "inline-flex",
          flexShrink: 0,
          fontSize: 18,
          color: style.solid,
          "& svg": { fontSize: "inherit", width: "1em", height: "1em" },
        }}
      >
        {icon || <Icon />}
      </Box>
      <Box
        sx={{
          flex: 1,
          minWidth: 0,
          display: "flex",
          flexDirection: "column",
          gap: "4px",
        }}
      >
        {title && (
          <Box
            sx={{
              fontFamily: '"Lexend", sans-serif',
              fontSize: 13,
              fontWeight: 600,
              lineHeight: 1.25,
              letterSpacing: "0.1px",
            }}
          >
            {title}
          </Box>
        )}
        {children && (
          <Box
            sx={{
              fontFamily: '"Lexend", sans-serif',
              fontSize: 12,
              fontWeight: 400,
              lineHeight: 1.35,
              letterSpacing: "0.15px",
            }}
          >
            {children}
          </Box>
        )}
        {action && (
          <ButtonBase
            component={action.href ? "a" : "button"}
            href={action.href}
            onClick={action.onClick}
            sx={linkSx(style.solid)}
          >
            {action.label}
          </ButtonBase>
        )}
      </Box>
      {onDismiss && (
        <ButtonBase
          aria-label="Dismiss"
          onClick={onDismiss}
          sx={{
            flexShrink: 0,
            fontSize: 16,
            color: style.content,
            "& svg": { fontSize: "inherit" },
          }}
        >
          <CloseIcon />
        </ButtonBase>
      )}
    </Box>
  );
};

export default Banner;
